import { Timestamp } from "firebase/firestore";
import { v4 as uuid4 } from "uuid";
import { Blog } from "../../core/models/Blog";
import { CommentModel } from "../../core/models/Comment";
import { FirebaseBlogRepository } from "../../core/repositories/blog/implementations/FirebaseBlogRepository";
import IBlogRepository from "../../core/repositories/blog/interfaces/IBlogRepository";
import Cubit from "../../core/utils/cubit";
import { AuthCubit, IAuthCubit } from "../auth/AuthBloc";
import { IProcessingService, ProcessingService } from "../processing-service/ProcessingService";
import { BlogState } from "./blogStates";

abstract class IBlogDetailsCubit extends Cubit<BlogState> {
    abstract getBlogDetails: (id: string) => Promise<BlogState>;
    abstract addComment: (commentText: string) => Promise<BlogState>;
    abstract deleteComment: (commentID: string, commentIndex: number) => Promise<BlogState>;
}

class BlogDetailsCubit extends IBlogDetailsCubit {

    private blogRepository: IBlogRepository = new FirebaseBlogRepository();
    private authCubit: IAuthCubit = AuthCubit.getInstance();
    private processingService: IProcessingService = ProcessingService.getInstance();

    constructor(id: string) {
        super({ state: 'LOADING' });
        this.getBlogDetails(id);
    }

    getBlogDetails: (id: string) => Promise<BlogState> = async (id: string) => {
        try {
            this.emit({ state: 'LOADING' });
            this.processingService.startProcessing();
            const blog: Blog = await this.blogRepository.getBlog(id);
            this.emit({ state: 'LOADED', blog });
        } catch (error) {
            if (error instanceof Error) {
                this.emit({ state: 'NOT_FOUND' });
            } else {
                this.emit({ state: 'ERROR' });
            }
        }
        this.processingService.stopProcessing();
        return this.state;
    }
    addComment: (commentText: string) => Promise<BlogState> = async (commentText: string) => {
        try {
            let comment: CommentModel = {
                userID: this.authCubit.state.user!.id,
                id: uuid4(),
                blogId: this.state.blog!.id!,
                text: commentText,
                blogTitle: this.state.blog!.title,
                blogDescription: this.state.blog?.description,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
                username: this.authCubit.state.user!.username,
                userphoto: this.authCubit.state.user!.imageUrl,
            };
            this.emit({ state: 'ADDING_COMMENT', blog: this.state.blog, addedComment: comment });
            comment = await this.blogRepository.addComment(this.state.blog!.id!, comment);
            this.state.blog?.comments?.unshift(comment);
            this.emit({ state: 'LOADED', blog: this.state.blog });
        } catch (error: any) {
            this.emit({ state: 'ERROR', error: { ...error } })
        }
        return this.state;
    }
    deleteComment: (commentID: string, index: number) => Promise<BlogState> = async (commentID: string, index: number) => {
        try {
            this.emit({ state: 'DELETING_COMMENT', blog: this.state.blog, deletedCommentID: commentID });
            await this.blogRepository.deleteComment(commentID);
            this.state.blog!.comments!.splice(index);
            this.emit({ state: 'LOADED', blog: this.state.blog });
        } catch (error: any) {
            this.emit({ state: 'ERROR', error: { ...error } });
        }
        return this.state;
    }
}

export { BlogDetailsCubit, IBlogDetailsCubit };
